// src/components/admin/products/tabs/CreateProducts.tsx
// Layout refeito: tipos em 4 colunas (Acesso Total, Ciclos, Combos, Decks)
// Select + Título lado a lado, botão Criar acima, sem scroll desnecessário

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Search, Edit2, Trash2, X, Plus, Info,
    BookOpen, Package, Layers, GraduationCap,
    Power, PowerOff, AlertTriangle, Globe, ChevronDown, ChevronUp, Eye, EyeOff,
    Share2, Copy, CheckCircle, ExternalLink, ToggleLeft, ToggleRight,
    ChevronRight, Zap, Star, Brain, FileText, Target, ArrowRight,
    Stethoscope, ScrollText, LayoutGrid, Grid2X2,
    Heart, Activity, Pill, Syringe, Microscope, Thermometer, Dna,
    FlaskConical, TestTube, Baby, HeartPulse, Bone, Scan,
    ClipboardList, Award, Lightbulb, ListChecks, Ear,
    Wind, HeartCrack, Waves, Droplets, Ambulance, Siren, Cross, BriefcaseMedical
} from 'lucide-react';
import './CreateProducts.css';

import { useAdminProducts, useAdminDecks, useAdminCycles, useInvalidate } from '../../../../hooks/useAdminQueries';
import { Product } from '../../../../types/marketplace.types';

interface CreateProductsProps {
    onShowToast: (message: string, type: 'success' | 'error') => void;
}

interface Cycle {
    _id: string;
    name: string;
    description: string;
    deckCount: number;
}

interface PlanForm {
    durationMonths: number | null;
    priceCents: number;
}

interface ContentItemData {
    _id: string;
    name: string;
    type: string;
    cardCount?: number;
    customDescription: string;
}

interface ContentDeck {
    _id: string;
    name: string;
    items: ContentItemData[];
}

// ── Editor de blocos de descrição ──────────────────────────────────────
export interface DescBlock {
    type: 'p' | 'li';
    icon?: string;
    text: string;
}

export const DESC_ICONS: { name: string; label: string; component: React.ComponentType<any>; group?: string }[] = [
    // Conteúdo / geral
    { name: 'CheckCircle',  label: 'Check',       component: CheckCircle,  group: 'geral' },
    { name: 'ChevronRight', label: 'Seta',        component: ChevronRight, group: 'geral' },
    { name: 'ArrowRight',   label: 'Seta →',      component: ArrowRight,   group: 'geral' },
    { name: 'BookOpen',     label: 'Livro',       component: BookOpen,     group: 'geral' },
    { name: 'FileText',     label: 'Arquivo',     component: FileText,     group: 'geral' },
    { name: 'ScrollText',   label: 'Leitura',     component: ScrollText,   group: 'geral' },
    { name: 'Layers',       label: 'Camadas',     component: Layers,       group: 'geral' },
    { name: 'Target',       label: 'Objetivo',    component: Target,       group: 'geral' },
    { name: 'Zap',          label: 'Destaque',    component: Zap,          group: 'geral' },
    { name: 'Star',         label: 'Estrela',     component: Star,         group: 'geral' },
    { name: 'Award',        label: 'Prêmio',      component: Award,        group: 'geral' },
    { name: 'Lightbulb',    label: 'Ideia',       component: Lightbulb,    group: 'geral' },
    { name: 'ListChecks',   label: 'Lista',       component: ListChecks,   group: 'geral' },
    { name: 'GraduationCap',label: 'Formação',    component: GraduationCap,group: 'geral' },
    { name: 'ClipboardList',label: 'Clipboard',   component: ClipboardList,group: 'geral' },
    // Médico
    { name: 'Stethoscope',  label: 'Estetoscópio',component: Stethoscope,  group: 'medico' },
    { name: 'Heart',        label: 'Coração',     component: Heart,        group: 'medico' },
    { name: 'HeartPulse',   label: 'ECG',         component: HeartPulse,   group: 'medico' },
    { name: 'Activity',     label: 'Atividade',   component: Activity,     group: 'medico' },
    { name: 'Brain',        label: 'Cérebro',     component: Brain,        group: 'medico' },
    { name: 'Pill',         label: 'Remédio',     component: Pill,         group: 'medico' },
    { name: 'Syringe',      label: 'Seringa',     component: Syringe,      group: 'medico' },
    { name: 'Microscope',   label: 'Microscópio', component: Microscope,   group: 'medico' },
    { name: 'Thermometer',  label: 'Termômetro',  component: Thermometer,  group: 'medico' },
    { name: 'Dna',          label: 'DNA',         component: Dna,          group: 'medico' },
    { name: 'FlaskConical', label: 'Frasco',      component: FlaskConical, group: 'medico' },
    { name: 'TestTube',     label: 'Tubo',        component: TestTube,     group: 'medico' },
    { name: 'Baby',         label: 'Pediatria',   component: Baby,         group: 'medico' },
    { name: 'Bone',         label: 'Ortopedia',   component: Bone,         group: 'medico' },
    { name: 'Scan',         label: 'Imagem',      component: Scan,         group: 'medico' },
    { name: 'Eye',          label: 'Oftalmo',     component: Eye,          group: 'medico' },
    { name: 'Ear',          label: 'Otorrino',    component: Ear,          group: 'medico' },
    // Cardiologia / Pneumologia
    { name: 'HeartCrack',   label: 'Cardiopatia', component: HeartCrack,   group: 'cardio' },
    { name: 'Wind',         label: 'Ventilação',  component: Wind,         group: 'cardio' },
    { name: 'Waves',        label: 'Ondas',       component: Waves,        group: 'cardio' },
    { name: 'Droplets',     label: 'Sangue',      component: Droplets,     group: 'cardio' },
    { name: 'Ambulance',    label: 'Urgência',    component: Ambulance,    group: 'cardio' },
    { name: 'Siren',        label: 'Emergência',  component: Siren,        group: 'cardio' },
    { name: 'Cross',        label: 'Cruz Médica', component: Cross,        group: 'cardio' },
    { name: 'BriefcaseMedical', label: 'Clínica', component: BriefcaseMedical, group: 'cardio' },
];

export function parseDescBlocks(str: string): DescBlock[] {
    if (!str) return [];
    try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) return parsed as DescBlock[];
    } catch { /* fallthrough */ }
    return [{ type: 'p', text: str }];
}

export function serializeDescBlocks(blocks: DescBlock[]): string {
    return JSON.stringify(blocks);
}

type ProductTypeOption = 'access_all' | 'cycle' | 'combo' | 'deck';

// Ordem: Acesso Total → Ciclos → Combos → Decks
const TYPE_ORDER: ProductTypeOption[] = ['access_all', 'cycle', 'combo', 'deck'];

const TYPE_CONFIG: Record<ProductTypeOption, { label: string; icon: React.ReactNode; description: string }> = {
    access_all: { label: 'Acesso Total', icon: <Globe size={20} />, description: 'Acesso a toda a plataforma pelo período do plano' },
    cycle: { label: 'Campo de Estudo', icon: <GraduationCap size={20} />, description: 'Ciclo completo de um campo acadêmico' },
    combo: { label: 'Combo de Decks', icon: <Layers size={20} />, description: 'Grupo de decks com tema relacionado' },
    deck: { label: 'Deck Individual', icon: <BookOpen size={20} />, description: 'Um único deck de flashcards' },
};

// ── Editor de blocos de descrição (ícone + texto) ─────────────────────
const DescBlockEditor: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
    const [blocks, setBlocks] = useState<DescBlock[]>(() => parseDescBlocks(value));
    const [openIconPicker, setOpenIconPicker] = useState<number | null>(null);

    const update = (newBlocks: DescBlock[]) => {
        setBlocks(newBlocks);
        onChange(serializeDescBlocks(newBlocks));
    };

    const addLi = () => update([...blocks, { type: 'li', icon: 'CheckCircle', text: '' }]);
    const addP  = () => update([...blocks, { type: 'p', text: '' }]);

    const del = (i: number) => update(blocks.filter((_, idx) => idx !== i));

    const setText = (i: number, text: string) => {
        const b = [...blocks]; b[i] = { ...b[i], text }; update(b);
    };

    const setIcon = (i: number, icon: string) => {
        const b = [...blocks]; b[i] = { ...b[i], icon }; update(b);
        setOpenIconPicker(null);
    };

    return (
        <div className="dbe-root">
            {blocks.map((block, i) => {
                const IconComp = block.type === 'li'
                    ? (DESC_ICONS.find(ic => ic.name === block.icon)?.component ?? CheckCircle)
                    : null;
                return (
                    <div key={i} className={`dbe-block ${block.type === 'li' ? 'dbe-block-li' : 'dbe-block-p'}`}>
                        {block.type === 'li' && (
                            <div className="dbe-icon-wrap">
                                <button type="button" className="dbe-icon-btn"
                                    onClick={() => setOpenIconPicker(openIconPicker === i ? null : i)}
                                    title="Trocar ícone">
                                    {IconComp && <IconComp size={15} />}
                                </button>
                                {openIconPicker === i && (
                                    <div className="dbe-icon-picker">
                                        {DESC_ICONS.map(ic => {
                                            const C = ic.component;
                                            return (
                                                <button key={ic.name} type="button"
                                                    className={`dbe-icon-pick-btn ${block.icon === ic.name ? 'active' : ''}`}
                                                    title={ic.label}
                                                    onClick={() => setIcon(i, ic.name)}>
                                                    <C size={14} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        <input
                            type="text"
                            className="dbe-input"
                            value={block.text}
                            onChange={e => setText(i, e.target.value)}
                            placeholder={block.type === 'li' ? 'Tópico...' : 'Parágrafo de texto...'}
                        />
                        <button type="button" className="dbe-del-btn" onClick={() => del(i)} title="Remover">
                            <X size={13} />
                        </button>
                    </div>
                );
            })}
            <div className="dbe-actions">
                <button type="button" className="dbe-add-li" onClick={addLi}>
                    <Plus size={12} /> Tópico com ícone
                </button>
                <button type="button" className="dbe-add-p" onClick={addP}>
                    <Plus size={12} /> Parágrafo
                </button>
            </div>
        </div>
    );
};

const CreateProducts: React.FC<CreateProductsProps> = ({ onShowToast }) => {

    const [formOpen, setFormOpen] = useState(true);
    const [productType, setProductType] = useState<ProductTypeOption>('access_all');
    const [selectedDeckId, setSelectedDeckId] = useState('');
    const [selectedCycleId, setSelectedCycleId] = useState('');
    const [selectedDeckIds, setSelectedDeckIds] = useState<Array<string | { id: string; type: string }>>([]);
    const [productTitle, setProductTitle] = useState('');
    const [plans, setPlans] = useState<PlanForm[]>([{ durationMonths: 6, priceCents: 10000 }]);
    const [creatingProduct, setCreatingProduct] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editPlans, setEditPlans] = useState<PlanForm[]>([]);
    const [comboDecks, setComboDecks] = useState<any[]>([]);
    const [showAddDecksModal, setShowAddDecksModal] = useState(false);
    const [selectedDecksToAdd, setSelectedDecksToAdd] = useState<string[]>([]);
    const [loadingComboDecks, setLoadingComboDecks] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    // ── Estado do modal de link de venda ──
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkProduct, setLinkProduct] = useState<Product | null>(null);
    const [linkConfig, setLinkConfig] = useState({
        enabled: false,
        pixKey: '',
        whatsappNumber: '',
        pixHolderName: '',
        showPrice: true,
        showStats: true,
        slug: '',
        previewEnabled: false,
        previewButtonText: 'Acessar prévias grátis',
        previewButtonUrl: '',
        previewNotice: '',
        subdeckDescriptions: {} as Record<string, string>,
        subdeckIcons: {} as Record<string, string>,
        deckDescriptions: {} as Record<string, string>,
        subdeckPreviewLinks: {} as Record<string, string>,
        gridCols: 3 as 2 | 3,
        heroDescription: '',
        statsMode: 'auto' as 'auto' | 'custom',
        statsCustomContent: '',
        differentialEnabled: false,
        differentialTitle: 'Como funciona?',
        differentialContent: '',
        differentialRightMode: 'none' as 'none' | 'animation_osce' | 'stat_cards',
        differentialStatCards: [
            { icon: 'BookOpen', value: '', label: '' },
            { icon: 'Stethoscope', value: '', label: '' },
            { icon: 'Zap', value: '', label: '' },
            { icon: 'Brain', value: '', label: '' }
        ] as Array<{ icon: string; value: string; label: string }>
    });
    const [savingLink, setSavingLink] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [contentStructure, setContentStructure] = useState<ContentDeck[]>([]);
    const [loadingContent, setLoadingContent] = useState(false);
    const [contentError, setContentError] = useState<string | null>(null);
    const [showDescsModal, setShowDescsModal] = useState(false);
    const [localDescs, setLocalDescs] = useState<Record<string, string>>({});
    const [localIcons, setLocalIcons] = useState<Record<string, string>>({});
    const [localDeckDescs, setLocalDeckDescs] = useState<Record<string, string>>({});
    const [localPreviewLinks, setLocalPreviewLinks] = useState<Record<string, string>>({});
    const [openIconPickerFor, setOpenIconPickerFor] = useState<string | null>(null);
    const [statCardIconPicker, setStatCardIconPicker] = useState<number | null>(null);

    const { data: productsData, isLoading: loadingProducts } = useAdminProducts();
    const { data: decksData, isLoading: loadingDecks } = useAdminDecks();
    const { data: cyclesData } = useAdminCycles();
    const invalidate = useInvalidate();

    const products = productsData?.products ?? [];
    const decks = decksData?.decks ?? [];
    const cycles = (cyclesData ?? []) as Cycle[];
    const loading = loadingProducts || loadingDecks;

    const formatPrice = (cents: number) => `R$ ${(cents / 100).toFixed(2)}`;

    const getDurationLabel = (months: number | null) => {
        if (months === null) return 'Vitalício';
        if (months <= 0.01) return '1h (teste)';
        if (months <= 0.04) return '1 dia (teste)';
        if (months === 1) return '1 mês';
        if (months < 12) return `${months} meses`;
        return `${months / 12} ano(s)`;
    };

    const getTypeLabel = (type: string) => TYPE_CONFIG[type as ProductTypeOption]?.label ?? type;
    const getTypeIcon = (type: string) => TYPE_CONFIG[type as ProductTypeOption]?.icon ?? <Package size={14} />;

    const getFilteredProducts = () => {
        if (!searchTerm.trim()) return products;
        const t = searchTerm.toLowerCase();
        return products.filter((p: Product) =>
            p.title.toLowerCase().includes(t) || p.description?.toLowerCase().includes(t)
        );
    };

    const addPlan = () => setPlans([...plans, { durationMonths: 12, priceCents: 15000 }]);
    const removePlan = (i: number) => plans.length > 1 && setPlans(plans.filter((_, idx) => idx !== i));
    const updatePlan = (i: number, field: keyof PlanForm, value: any) => {
        const p = [...plans]; p[i] = { ...p[i], [field]: value }; setPlans(p);
    };

    const addEditPlan = () => setEditPlans([...editPlans, { durationMonths: 12, priceCents: 15000 }]);
    const removeEditPlan = (i: number) => editPlans.length > 1 && setEditPlans(editPlans.filter((_, idx) => idx !== i));
    const updateEditPlan = (i: number, field: keyof PlanForm, value: any) => {
        const p = [...editPlans]; p[i] = { ...p[i], [field]: value }; setEditPlans(p);
    };

    const resetForm = () => {
        setProductType('access_all');
        setSelectedDeckId('');
        setSelectedCycleId('');
        setSelectedDeckIds([]);
        setProductTitle('');
        setPlans([{ durationMonths: 6, priceCents: 10000 }]);
    };

    const openDeleteModal = (productId: string, title: string) => {
        setProductToDelete({ id: productId, title });
        setShowDeleteModal(true);
    };

    const handleDeleteProduct = async () => {
        if (!productToDelete) return;
        try {
            setDeleting(true);
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/marketplace/products/${productToDelete.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                onShowToast(
                    data.purchasesKept > 0
                        ? `Produto removido. ${data.purchasesKept} comprador(es) mantêm acesso.`
                        : 'Produto removido com sucesso',
                    'success'
                );
                setShowDeleteModal(false);
                setProductToDelete(null);
                invalidate.products();
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao remover produto', 'error');
            }
        } catch { onShowToast('Erro ao remover produto', 'error'); }
        finally { setDeleting(false); }
    };

    const handleCreateProduct = async () => {
        if (productType === 'deck' && !selectedDeckId) { onShowToast('Selecione um deck', 'error'); return; }
        if (productType === 'combo' && selectedDeckIds.length === 0) { onShowToast('Selecione pelo menos um deck', 'error'); return; }
        if (productType === 'cycle' && !selectedCycleId) { onShowToast('Selecione um campo de estudo', 'error'); return; }
        if (!productTitle.trim()) { onShowToast('Digite um título', 'error'); return; }
        if (plans.some(p => p.priceCents <= 0)) { onShowToast('Todos os planos devem ter preço > 0', 'error'); return; }

        try {
            setCreatingProduct(true);
            const selectedDeck = decks.find((d: any) => d._id === selectedDeckId);
            const isCustom = selectedDeck && 'type' in selectedDeck && selectedDeck.type === 'custom_deck';
            const finalType = productType === 'deck' && isCustom ? 'custom_deck' : productType;

            let refId: string | null = null;
            let deckIds: any[] | undefined = undefined;
            if (productType === 'deck') refId = selectedDeckId;
            else if (productType === 'cycle') refId = selectedCycleId;
            else if (productType === 'combo') deckIds = selectedDeckIds;

            const token = localStorage.getItem('accessToken');
            const res = await fetch('/api/admin/marketplace/products', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ type: finalType, refId, deckIds, title: productTitle.trim(), plans })
            });
            if (res.ok) {
                onShowToast('Produto criado com sucesso!', 'success');
                resetForm();
                invalidate.products();
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao criar produto', 'error');
            }
        } catch { onShowToast('Erro ao criar produto', 'error'); }
        finally { setCreatingProduct(false); }
    };

    const toggleProductActive = async (productId: string, currentStatus: boolean) => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/marketplace/products/${productId}/toggle`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                onShowToast(currentStatus ? 'Produto desativado' : 'Produto ativado', 'success');
                invalidate.products();
            }
        } catch { onShowToast('Erro ao alterar status', 'error'); }
    };

    const toggleProductVisibility = async (productId: string, currentlyVisible: boolean) => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/marketplace/products/${productId}/toggle-visibility`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                onShowToast(
                    currentlyVisible ? 'Produto ocultado do marketplace' : 'Produto visível no marketplace',
                    'success'
                );
                invalidate.products();
            }
        } catch { onShowToast('Erro ao alternar visibilidade', 'error'); }
    };

    const openEditModal = async (product: Product) => {
        setEditingProduct(product);
        setEditTitle(product.title);
        setEditDescription(product.description || '');
        setEditPlans(product.plans.map((p: any) => ({ durationMonths: p.durationMonths, priceCents: p.priceCents })));
        if (product.type === 'combo') await loadComboDecks(product.refId!);
        else setComboDecks([]);
        setShowEditModal(true);
    };

    const loadComboDecks = async (comboId: string) => {
        try {
            setLoadingComboDecks(true);
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/combos/${comboId}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) { const data = await res.json(); setComboDecks(data.combo.decks || []); }
        } catch { console.error('Erro ao carregar combo'); }
        finally { setLoadingComboDecks(false); }
    };

    const handleEditProduct = async () => {
        if (!editingProduct) return;
        if (!editTitle.trim()) { onShowToast('Digite um título', 'error'); return; }
        if (editPlans.some(p => p.priceCents <= 0)) { onShowToast('Todos os planos devem ter preço > 0', 'error'); return; }
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/marketplace/products/${editingProduct._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ title: editTitle.trim(), description: editDescription.trim(), plans: editPlans })
            });
            if (res.ok) {
                onShowToast('Produto atualizado!', 'success');
                setShowEditModal(false);
                setEditingProduct(null);
                invalidate.products();
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao atualizar', 'error');
            }
        } catch { onShowToast('Erro ao atualizar produto', 'error'); }
    };

    const handleAddDecksToCombo = async () => {
        if (!editingProduct || selectedDecksToAdd.length === 0) { onShowToast('Selecione pelo menos um deck', 'error'); return; }
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/marketplace/products/${editingProduct._id}/add-decks`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ deckIds: selectedDecksToAdd })
            });
            const data = await res.json();
            if (res.ok) {
                onShowToast(data.message, 'success');
                setShowAddDecksModal(false);
                setSelectedDecksToAdd([]);
                await loadComboDecks(editingProduct.refId!);
            } else { onShowToast(data.message || 'Erro ao adicionar decks', 'error'); }
        } catch { onShowToast('Erro ao adicionar decks', 'error'); }
    };

    // ── Link de venda ──
    const generateSlug = (title: string): string =>
        title.toLowerCase()
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9\s]/g, '').trim()
            .replace(/\s+/g, '-').replace(/-+/g, '-');

    const loadContentStructure = async (productId: string) => {
        setLoadingContent(true);
        setContentError(null);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/marketplace/products/${productId}/content-structure`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('[cs] productId:', productId, 'status:', res.status, 'ok:', res.ok);
            if (res.ok) {
                const data = await res.json();
                console.log('[cs] decks:', JSON.stringify(data));
                setContentStructure(data.decks || []);
                const descs: Record<string, string> = {};
                for (const deck of (data.decks || []) as ContentDeck[]) {
                    for (const item of deck.items) {
                        descs[item._id] = item.customDescription || '';
                    }
                }
                setLinkConfig(prev => ({ ...prev, subdeckDescriptions: descs }));
            } else {
                const errText = await res.text().catch(() => '');
                console.log('[cs] error body:', errText);
                setContentError(`Erro ${res.status} ao carregar estrutura`);
            }
        } catch (err) {
            console.error('[cs] catch:', err);
            setContentError('Erro de conexão ao carregar descrições');
        }
        finally { setLoadingContent(false); }
    };

    useEffect(() => {
        if (showLinkModal && linkProduct?._id) {
            loadContentStructure(linkProduct._id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showLinkModal, linkProduct?._id]);

    const openLinkModal = (product: Product) => {
        const pp = (product as any).publicPage || {};
        setLinkProduct(product);
        setContentStructure([]);
        setContentError(null);
        setLinkConfig({
            enabled: pp.enabled || false,
            pixKey: pp.pixKey || '77988362587',
            whatsappNumber: pp.whatsappNumber || '5577988362587',
            pixHolderName: pp.pixHolderName || '',
            showPrice: pp.showPrice !== false,
            showStats: pp.showStats !== false,
            slug: (product as any).publicSlug || generateSlug(product.title),
            previewEnabled: pp.previewEnabled || false,
            previewButtonText: pp.previewButtonText || 'Acessar prévias grátis',
            previewButtonUrl: pp.previewButtonUrl || '/login',
            previewNotice: pp.previewNotice || '',
            subdeckDescriptions: pp.subdeckDescriptions || {} as Record<string, string>,
            subdeckIcons: pp.subdeckIcons || {} as Record<string, string>,
            deckDescriptions: pp.deckDescriptions || {} as Record<string, string>,
            subdeckPreviewLinks: pp.subdeckPreviewLinks || {} as Record<string, string>,
            gridCols: (pp.gridCols === 2 ? 2 : 3) as 2 | 3,
            heroDescription: pp.heroDescription || '',
            statsMode: (pp.statsMode === 'custom' ? 'custom' : 'auto') as 'auto' | 'custom',
            statsCustomContent: pp.statsCustomContent || '',
            differentialEnabled: pp.differentialEnabled || false,
            differentialTitle: pp.differentialTitle || 'Como funciona?',
            differentialContent: pp.differentialContent || '',
            differentialRightMode: (['none','animation_osce','stat_cards'].includes(pp.differentialRightMode) ? pp.differentialRightMode : 'none') as 'none' | 'animation_osce' | 'stat_cards',
            differentialStatCards: (Array.isArray(pp.differentialStatCards) && pp.differentialStatCards.length === 4
                ? pp.differentialStatCards
                : [
                    { icon: 'BookOpen', value: '', label: '' },
                    { icon: 'Stethoscope', value: '', label: '' },
                    { icon: 'Zap', value: '', label: '' },
                    { icon: 'Brain', value: '', label: '' }
                ]) as Array<{ icon: string; value: string; label: string }>
        });
        setShowLinkModal(true);
    };

    const handleSaveLink = async () => {
        if (!linkProduct) return;
        setSavingLink(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/admin/marketplace/products/${linkProduct._id}/public-page`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(linkConfig)
            });
            if (res.ok) {
                const data = await res.json();
                onShowToast('Link de venda configurado!', 'success');
                setLinkConfig(prev => ({ ...prev, slug: data.publicSlug }));
                invalidate.products();
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao salvar', 'error');
            }
        } catch { onShowToast('Erro ao salvar link', 'error'); }
        finally { setSavingLink(false); }
    };

    const copyPublicLink = async (slug: string) => {
        const url = `${window.location.origin}/produto/${slug}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        } catch { onShowToast('Não foi possível copiar', 'error'); }
    };

    if (loading) return (
        <div className="pm-loading"><div className="pm-spinner" /><p>Carregando...</p></div>
    );

    const displayProducts = getFilteredProducts();

    // Determina se select e título ficam lado a lado ou só título
    const hasRefSelect = productType === 'deck' || productType === 'cycle';

    return (
        <div className="pm-container">

            {/* ══ FORMULÁRIO INLINE ══ */}
            <div className="pm-inline-form-wrap">

                {/* Toggle */}
                <button className="pm-inline-form-toggle" onClick={() => setFormOpen(v => !v)}>
                    <div className="pm-inline-form-toggle-left">
                        <Plus size={16} />
                        <span>Criar Novo Produto</span>
                    </div>
                    {formOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </button>

                {formOpen && (
                    <div className="pm-inline-form">

                        {/* ── Ações no topo ── */}
                        <div className="pm-inline-form-actions">
                            <button className="pm-btn-cancel-inline" onClick={resetForm} type="button">
                                Limpar
                            </button>
                            <button
                                className="pm-btn-create-inline"
                                onClick={handleCreateProduct}
                                disabled={creatingProduct}
                                type="button"
                            >
                                {creatingProduct
                                    ? <><div className="pm-spinner-small" /> Criando...</>
                                    : <><Plus size={14} /> Criar Produto</>
                                }
                            </button>
                        </div>

                        {/* ── Tipo — 4 colunas: Acesso Total, Ciclos, Combos, Decks ── */}
                        <div className="pm-form-group">
                            <label className="pm-label">Tipo de Produto</label>
                            <div className="pm-type-selector">
                                {TYPE_ORDER.map(t => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={`pm-type-btn ${productType === t ? 'pm-active' : ''}`}
                                        onClick={() => {
                                            setProductType(t);
                                            setSelectedDeckId('');
                                            setSelectedCycleId('');
                                            setSelectedDeckIds([]);
                                            setProductTitle(t === 'access_all' ? 'Acesso Total GRAIA' : '');
                                        }}
                                    >
                                        {TYPE_CONFIG[t].icon}
                                        <span>{TYPE_CONFIG[t].label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* ── Info: Acesso Total ── */}
                        {productType === 'access_all' && (
                            <div className="pm-info-box pm-access-all-info">
                                <Globe size={13} />
                                <span><strong>Acesso Total</strong> libera todos os conteúdos da plataforma pelo período do plano.</span>
                            </div>
                        )}

                        {/* ── Combo: lista de decks ── */}
                        {productType === 'combo' && (
                            <div className="pm-form-group">
                                <label className="pm-label">Decks do Combo *</label>
                                <div className="pm-decks-list">
                                    {decks.filter((d: any) => d.active !== false).map((deck: any) => (
                                        <label key={deck._id} className="pm-deck-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={selectedDeckIds.some((item: any) =>
                                                    (typeof item === 'string' ? item : item.id) === deck._id
                                                )}
                                                onChange={e => {
                                                    if (e.target.checked)
                                                        setSelectedDeckIds([...selectedDeckIds, { id: deck._id, type: deck.type || 'deck' }]);
                                                    else
                                                        setSelectedDeckIds(selectedDeckIds.filter((item: any) =>
                                                            (typeof item === 'string' ? item : item.id) !== deck._id
                                                        ));
                                                }}
                                            />
                                            <BookOpen size={14} />
                                            <div className="pm-deck-info">
                                                <strong>{deck.name}</strong>
                                                <span>{deck.totalCards} cards</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                {selectedDeckIds.length > 0 && (
                                    <div className="pm-selected-count">✓ {selectedDeckIds.length} deck(s) selecionado(s)</div>
                                )}
                            </div>
                        )}

                        {/* ── Select (deck ou ciclo) + Título — lado a lado ── */}
                        <div className={`pm-row-two ${!hasRefSelect ? 'pm-row-single' : ''}`}>

                            {productType === 'deck' && (
                                <div className="pm-form-group">
                                    <label className="pm-label">Deck Base *</label>
                                    <select
                                        value={selectedDeckId}
                                        onChange={e => {
                                            setSelectedDeckId(e.target.value);
                                            const d = decks.find((d: any) => d._id === e.target.value);
                                            if (d) setProductTitle((d as any).name);
                                        }}
                                        className="pm-form-select"
                                    >
                                        <option value="">Selecione um deck</option>
                                        {decks.filter((d: any) => d.active !== false).map((deck: any) => (
                                            <option key={deck._id} value={deck._id}>
                                                {deck.name} ({deck.totalCards} cards)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {productType === 'cycle' && (
                                <div className="pm-form-group">
                                    <label className="pm-label">Campo de Estudo *</label>
                                    <select
                                        value={selectedCycleId}
                                        onChange={e => {
                                            setSelectedCycleId(e.target.value);
                                            const c = cycles.find(c => c._id === e.target.value);
                                            if (c) setProductTitle(c.name);
                                        }}
                                        className="pm-form-select"
                                    >
                                        <option value="">Selecione um campo</option>
                                        {cycles.map(c => (
                                            <option key={c._id} value={c._id}>
                                                {c.name} ({c.deckCount} decks)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="pm-form-group">
                                <label className="pm-label">Título do Produto *</label>
                                <input
                                    type="text"
                                    value={productTitle}
                                    onChange={e => setProductTitle(e.target.value)}
                                    placeholder="Ex: Acesso Total GRAIA"
                                    className="pm-form-input"
                                />
                            </div>
                        </div>

                        {/* ── Planos ── */}
                        <div className="pm-form-group">
                            <div className="pm-plans-header">
                                <label className="pm-label" style={{ margin: 0 }}>Planos de Pagamento *</label>
                                <button className="pm-btn-add-plan" onClick={addPlan} type="button">
                                    <Plus size={12} /> Adicionar Plano
                                </button>
                            </div>
                            <div className="pm-plans-inline">
                                {plans.map((plan, index) => (
                                    <div key={index} className="pm-plan-form">
                                        <div className="pm-plan-fields">
                                            <div className="pm-plan-field">
                                                <label>Duração</label>
                                                <select
                                                    value={plan.durationMonths ?? 'null'}
                                                    onChange={e => updatePlan(index, 'durationMonths',
                                                        e.target.value === 'null' ? null : parseFloat(e.target.value)
                                                    )}
                                                    className="pm-form-select"
                                                >
                                                    <option value="0.001">1 hora (teste)</option>
                                                    <option value="0.033">1 dia (teste)</option>
                                                    <option value="1">1 mês</option>
                                                    <option value="3">3 meses</option>
                                                    <option value="4">4 meses</option>
                                                    <option value="6">6 meses</option>
                                                    <option value="12">12 meses</option>
                                                    <option value="null">Vitalício</option>
                                                </select>
                                            </div>
                                            <div className="pm-plan-field">
                                                <label>Preço (R$)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={(plan.priceCents / 100).toFixed(2)}
                                                    onChange={e => updatePlan(index, 'priceCents',
                                                        Math.round(parseFloat(e.target.value) * 100)
                                                    )}
                                                    className="pm-form-input"
                                                />
                                            </div>
                                        </div>
                                        {plans.length > 1 && (
                                            <button className="pm-btn-remove-plan" onClick={() => removePlan(index)} type="button">
                                                <Trash2 size={12} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                )}
            </div>

            {/* ══ SEPARADOR ══ */}
            <div className="pm-section-divider">
                <span>Produtos Cadastrados</span>
                <span className="pm-products-count">{products.length} produto(s)</span>
            </div>

            {/* ══ BUSCA ══ */}
            <div className="pm-search-box">
                <Search size={16} />
                <input
                    type="text"
                    placeholder="Buscar por nome..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                    <button className="pm-clear-search" onClick={() => setSearchTerm('')}>
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* ══ GRID ══ */}
            {displayProducts.length === 0 ? (
                <div className="pm-empty">
                    <Package size={40} />
                    <h4>{searchTerm.trim() ? 'Nenhum produto encontrado' : 'Nenhum produto criado'}</h4>
                    <p>{searchTerm.trim() ? 'Tente outros termos' : 'Use o formulário acima para criar'}</p>
                </div>
            ) : (
                <div className="pm-products-grid">
                    {displayProducts.map((product: Product) => (
                        <div
                            key={product._id}
                            className={`pm-product-card ${!product.active ? 'pm-inactive' : ''} ${product.type === 'access_all' ? 'pm-access-all' : ''}`}
                        >
                            <div className="pm-product-header">
                                <div className="pm-product-type-badge">
                                    {getTypeIcon(product.type)}{getTypeLabel(product.type)}
                                </div>
                                <div className="pm-product-actions">
                                    <button className="pm-btn-icon" onClick={() => openEditModal(product)} title="Editar">
                                        <Edit2 size={13} />
                                    </button>

                                    <button
                                        className={`pm-btn-icon pm-btn-link ${(product as any).publicPage?.enabled ? 'pm-link-active' : ''}`}
                                        onClick={() => openLinkModal(product)}
                                        title="Gerar Link de Venda"
                                    >
                                        <Share2 size={13} />
                                    </button>

                                    {/* ✅ NOVO: toggle de visibilidade */}
                                    <button
                                        className={`pm-btn-toggle ${product.visible !== false ? 'pm-visible' : 'pm-hidden'}`}
                                        onClick={() => toggleProductVisibility(product._id, product.visible !== false)}
                                        title={product.visible !== false ? 'Ocultar do marketplace' : 'Mostrar no marketplace'}
                                    >
                                        {product.visible !== false ? <Eye size={13} /> : <EyeOff size={13} />}
                                    </button>

                                    <button
                                        className={`pm-btn-toggle ${product.active ? 'pm-active' : ''}`}
                                        onClick={() => toggleProductActive(product._id, product.active)}
                                        title={product.active ? 'Desativar' : 'Ativar'}
                                    >
                                        {product.active ? <Power size={13} /> : <PowerOff size={13} />}
                                    </button>
                                    <button className="pm-btn-icon pm-danger" onClick={() => openDeleteModal(product._id, product.title)} title="Remover">
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </div>

                            <h4 className="pm-product-title">{product.title}</h4>

                            <div className="pm-product-plans">
                                {product.plans.map((plan: PlanForm, idx: number) => (
                                    <div key={idx} className="pm-plan-item">
                                        <span>{getDurationLabel(plan.durationMonths)}</span>
                                        <span className="pm-plan-price">{formatPrice(plan.priceCents)}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="pm-product-footer">
                                {product.visible === false && (
                                    <span className="pm-product-hidden-badge">
                                        <EyeOff size={10} /> Oculto
                                    </span>
                                )}

                                {product.refDetails?.name?.includes('⚠️ Deck Removido') && (
                                    <span className="pm-product-orphan"><AlertTriangle size={11} /> Órfão</span>
                                )}
                                <span className={`pm-product-status ${product.active ? 'pm-active' : 'pm-inactive'}`}>
                                    {product.active ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ══ MODAL EDITAR ══ */}
            {showEditModal && editingProduct && createPortal(
                <div className="pm-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="pm-modal-content pm-large" onClick={e => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>Editar Produto</h3>
                            <button className="pm-btn-close-modal" onClick={() => setShowEditModal(false)}><X size={18} /></button>
                        </div>
                        <div className="pm-modal-body">
                            {editingProduct.type === 'access_all' && (
                                <div className="pm-info-box pm-access-all-info" style={{ marginBottom: 18 }}>
                                    <Globe size={13} />
                                    <span>Produto de <strong>Acesso Total</strong>. Edite título, descrição e planos.</span>
                                </div>
                            )}

                            {editingProduct.type === 'combo' && (
                                <div className="pm-form-group">
                                    <label>Decks do Combo</label>
                                    {loadingComboDecks ? (
                                        <div className="pm-loading-combo">
                                            <div className="pm-spinner-small" /><span>Carregando...</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="pm-combo-decks">
                                                {comboDecks.length === 0
                                                    ? <p className="pm-no-decks">Nenhum deck neste combo</p>
                                                    : comboDecks.map(deck => (
                                                        <div key={deck._id} className="pm-combo-deck-item">
                                                            <BookOpen size={13} />
                                                            <div className="pm-deck-info">
                                                                <strong>{deck.name}</strong>
                                                                <span>{deck.totalCards || 0} cards</span>
                                                            </div>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                            <button type="button" className="pm-btn-add-decks" onClick={() => setShowAddDecksModal(true)}>
                                                <Plus size={13} /> Adicionar Mais Decks
                                            </button>
                                            <div className="pm-info-box">
                                                <Info size={12} />
                                                <span>Quem já comprou <strong>ganha acesso automaticamente</strong> aos novos decks.</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            <div className="pm-form-group">
                                <label>Título *</label>
                                <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} className="pm-form-input" />
                            </div>

                            <div className="pm-form-group">
                                <label>Descrição</label>
                                <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} className="pm-form-textarea" rows={3} />
                            </div>

                            <div className="pm-form-group">
                                <div className="pm-plans-header">
                                    <label style={{ margin: 0 }}>Planos *</label>
                                    <button className="pm-btn-add-plan" onClick={addEditPlan} type="button">
                                        <Plus size={12} /> Adicionar Plano
                                    </button>
                                </div>
                                {editPlans.map((plan, index) => (
                                    <div key={index} className="pm-plan-form">
                                        <div className="pm-plan-fields">
                                            <div className="pm-plan-field">
                                                <label>Duração</label>
                                                <select
                                                    value={plan.durationMonths ?? 'null'}
                                                    onChange={e => updateEditPlan(index, 'durationMonths',
                                                        e.target.value === 'null' ? null : parseInt(e.target.value)
                                                    )}
                                                    className="pm-form-select"
                                                >
                                                    <option value="1">1 mês</option>
                                                    <option value="3">3 meses</option>
                                                    <option value="4">4 meses</option>
                                                    <option value="6">6 meses</option>
                                                    <option value="12">12 meses</option>
                                                    <option value="null">Vitalício</option>
                                                </select>
                                            </div>
                                            <div className="pm-plan-field">
                                                <label>Preço (R$)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={(plan.priceCents / 100).toFixed(2)}
                                                    onChange={e => updateEditPlan(index, 'priceCents',
                                                        Math.round(parseFloat(e.target.value) * 100)
                                                    )}
                                                    className="pm-form-input"
                                                />
                                            </div>
                                        </div>
                                        {editPlans.length > 1 && (
                                            <button className="pm-btn-remove-plan" onClick={() => removeEditPlan(index)} type="button">
                                                <Trash2 size={12} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="pm-modal-footer">
                            <button className="pm-btn-cancel" onClick={() => setShowEditModal(false)}>Cancelar</button>
                            <button className="pm-btn-confirm" onClick={handleEditProduct}>Salvar Alterações</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ══ MODAL ADICIONAR DECKS ══ */}
            {showAddDecksModal && editingProduct && createPortal(
                <div className="pm-modal-overlay" onClick={() => setShowAddDecksModal(false)}>
                    <div className="pm-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>Adicionar Decks ao Combo</h3>
                            <button className="pm-btn-close-modal" onClick={() => setShowAddDecksModal(false)}><X size={18} /></button>
                        </div>
                        <div className="pm-modal-body">
                            <p className="pm-modal-hint">Adicionando em "<strong>{editingProduct.title}</strong>"</p>
                            <div className="pm-decks-list">
                                {decks
                                    .filter((d: any) => d.active !== false && !comboDecks.some(cd => cd._id === d._id))
                                    .map((deck: any) => (
                                        <label key={deck._id} className="pm-deck-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={selectedDecksToAdd.includes(deck._id)}
                                                onChange={e => {
                                                    if (e.target.checked) setSelectedDecksToAdd([...selectedDecksToAdd, deck._id]);
                                                    else setSelectedDecksToAdd(selectedDecksToAdd.filter(id => id !== deck._id));
                                                }}
                                            />
                                            <BookOpen size={13} />
                                            <div className="pm-deck-info">
                                                <strong>{deck.name}</strong>
                                                <span>{deck.totalCards || 0} cards</span>
                                            </div>
                                        </label>
                                    ))
                                }
                            </div>
                            {selectedDecksToAdd.length > 0 && (
                                <div className="pm-selected-count">✓ {selectedDecksToAdd.length} selecionado(s)</div>
                            )}
                        </div>
                        <div className="pm-modal-footer">
                            <button className="pm-btn-cancel" onClick={() => { setShowAddDecksModal(false); setSelectedDecksToAdd([]); }}>
                                Cancelar
                            </button>
                            <button className="pm-btn-confirm" onClick={handleAddDecksToCombo} disabled={selectedDecksToAdd.length === 0}>
                                <Plus size={13} /> Adicionar {selectedDecksToAdd.length > 0 ? `(${selectedDecksToAdd.length})` : ''}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ══ MODAL LINK DE VENDA ══ */}
            {showLinkModal && linkProduct && createPortal(
                <div className="pm-modal-overlay" onClick={() => setShowLinkModal(false)}>
                    <div className="pm-modal-content pm-large pm-link-modal" onClick={e => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>
                                <Share2 size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                Link de Venda
                            </h3>
                            <button className="pm-btn-close-modal" onClick={() => setShowLinkModal(false)}><X size={18} /></button>
                        </div>

                        <div className="pm-modal-body">
                            <p className="pm-modal-hint" style={{ marginBottom: 20 }}>
                                Configure a página pública de venda para <strong>{linkProduct.title}</strong>.
                            </p>

                            {/* Toggle ativo */}
                            <div className="pm-link-toggle-row">
                                <div className="pm-link-toggle-text">
                                    <strong>Página pública ativa</strong>
                                    <span>Quando ativo, o link fica acessível para qualquer pessoa</span>
                                </div>
                                <button
                                    type="button"
                                    className={`pm-link-toggle-btn ${linkConfig.enabled ? 'active' : ''}`}
                                    onClick={() => setLinkConfig(p => ({ ...p, enabled: !p.enabled }))}
                                >
                                    {linkConfig.enabled ? <ToggleRight size={26} /> : <ToggleLeft size={26} />}
                                </button>
                            </div>

                            {/* Slug / URL */}
                            <div className="pm-form-group" style={{ marginTop: 16 }}>
                                <label>Slug da URL</label>
                                <input
                                    type="text"
                                    value={linkConfig.slug}
                                    onChange={e => setLinkConfig(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                                    className="pm-form-input"
                                    placeholder="ex: combo-pneumologia"
                                />
                                <p className="pm-field-hint">
                                    URL final: <code>{window.location.origin}/produto/{linkConfig.slug || '...'}</code>
                                </p>
                            </div>

                            {/* Copiar / abrir link */}
                            {linkConfig.slug && (
                                <div className="pm-link-actions-row">
                                    <button
                                        type="button"
                                        className="pm-btn-copy-link"
                                        onClick={() => copyPublicLink(linkConfig.slug)}
                                    >
                                        {copiedLink ? <><CheckCircle size={13} /> Copiado!</> : <><Copy size={13} /> Copiar link</>}
                                    </button>
                                    <a
                                        href={`/produto/${linkConfig.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="pm-btn-open-link"
                                    >
                                        <ExternalLink size={13} /> Abrir página
                                    </a>
                                </div>
                            )}

                            {/* ── Descrição do Hero ── */}
                            <div className="pm-link-section-label">Descrição do Hero</div>
                            <div className="pm-form-group">
                                <label>Subtítulo exibido abaixo do título na página pública</label>
                                <textarea
                                    value={linkConfig.heroDescription}
                                    onChange={e => setLinkConfig(p => ({ ...p, heroDescription: e.target.value }))}
                                    className="pm-form-textarea"
                                    rows={3}
                                    placeholder="Ex: Treine para o OSCE com cenários clínicos interativos..."
                                />
                                <p className="pm-field-hint">Este texto substitui a descrição do produto no hero da página de venda.</p>
                            </div>

                            {/* PIX */}
                            <div className="pm-link-section-label">Informações de Pagamento</div>

                            <div className="pm-row-two" style={{ marginBottom: 0 }}>
                                <div className="pm-form-group">
                                    <label>Chave PIX *</label>
                                    <input
                                        type="text"
                                        value={linkConfig.pixKey}
                                        onChange={e => setLinkConfig(p => ({ ...p, pixKey: e.target.value }))}
                                        className="pm-form-input"
                                        placeholder="77988362587"
                                    />
                                </div>
                                <div className="pm-form-group">
                                    <label>WhatsApp (com DDI)</label>
                                    <input
                                        type="text"
                                        value={linkConfig.whatsappNumber}
                                        onChange={e => setLinkConfig(p => ({ ...p, whatsappNumber: e.target.value }))}
                                        className="pm-form-input"
                                        placeholder="5577988362587"
                                    />
                                </div>
                            </div>

                            <div className="pm-form-group">
                                <label>Nome do titular (opcional)</label>
                                <input
                                    type="text"
                                    value={linkConfig.pixHolderName}
                                    onChange={e => setLinkConfig(p => ({ ...p, pixHolderName: e.target.value }))}
                                    className="pm-form-input"
                                    placeholder="Ex: João Silva"
                                />
                            </div>

                            {/* Mostrar preço */}
                            <div className="pm-link-toggle-row pm-link-toggle-row-sm">
                                <div className="pm-link-toggle-text">
                                    <strong>Exibir preço na página</strong>
                                    <span>Mostre os planos e valores cadastrados</span>
                                </div>
                                <button
                                    type="button"
                                    className={`pm-link-toggle-btn ${linkConfig.showPrice ? 'active' : ''}`}
                                    onClick={() => setLinkConfig(p => ({ ...p, showPrice: !p.showPrice }))}
                                >
                                    {linkConfig.showPrice ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                                </button>
                            </div>

                            {/* Mostrar estatísticas */}
                            <div className="pm-link-toggle-row pm-link-toggle-row-sm">
                                <div className="pm-link-toggle-text">
                                    <strong>Exibir seção de estatísticas</strong>
                                    <span>"O que você recebe" — contagem automática ou personalizada</span>
                                </div>
                                <button
                                    type="button"
                                    className={`pm-link-toggle-btn ${linkConfig.showStats ? 'active' : ''}`}
                                    onClick={() => setLinkConfig(p => ({ ...p, showStats: !p.showStats }))}
                                >
                                    {linkConfig.showStats ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                                </button>
                            </div>

                            {linkConfig.showStats && (
                                <div className="pm-stats-mode-wrap">
                                    <div className="pm-stats-mode-tabs">
                                        <button
                                            type="button"
                                            className={`pm-stats-mode-tab ${linkConfig.statsMode === 'auto' ? 'active' : ''}`}
                                            onClick={() => setLinkConfig(p => ({ ...p, statsMode: 'auto' }))}
                                        >
                                            <Zap size={13} /> Automático
                                        </button>
                                        <button
                                            type="button"
                                            className={`pm-stats-mode-tab ${linkConfig.statsMode === 'custom' ? 'active' : ''}`}
                                            onClick={() => setLinkConfig(p => ({ ...p, statsMode: 'custom' }))}
                                        >
                                            <Edit2 size={13} /> Personalizado
                                        </button>
                                    </div>
                                    {linkConfig.statsMode === 'auto' && (
                                        <p className="pm-field-hint" style={{ marginTop: 8 }}>
                                            Exibe contagens automáticas de decks, apostilas e flashcards do produto.
                                        </p>
                                    )}
                                    {linkConfig.statsMode === 'custom' && (
                                        <div style={{ marginTop: 10 }}>
                                            <p className="pm-field-hint" style={{ marginBottom: 8 }}>
                                                Use tópicos com ícone para listar o que o aluno recebe. Ex: "28 apostilas de OSCE", "Mais de 60 cenários clínicos"
                                            </p>
                                            <DescBlockEditor
                                                value={linkConfig.statsCustomContent}
                                                onChange={v => setLinkConfig(p => ({ ...p, statsCustomContent: v }))}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Bloco Diferencial ── */}
                            <div className="pm-link-section-label">Bloco Diferencial</div>
                            <div className="pm-link-toggle-row pm-link-toggle-row-sm">
                                <div className="pm-link-toggle-text">
                                    <strong>Exibir bloco de diferenciais</strong>
                                    <span>Seção com destaque visual para destacar o diferencial do produto</span>
                                </div>
                                <button
                                    type="button"
                                    className={`pm-link-toggle-btn ${linkConfig.differentialEnabled ? 'active' : ''}`}
                                    onClick={() => setLinkConfig(p => ({ ...p, differentialEnabled: !p.differentialEnabled }))}
                                >
                                    {linkConfig.differentialEnabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                                </button>
                            </div>

                            {linkConfig.differentialEnabled && (
                                <div className="pm-differential-wrap">
                                    <div className="pm-form-group">
                                        <label>Título do bloco</label>
                                        <input
                                            type="text"
                                            value={linkConfig.differentialTitle}
                                            onChange={e => setLinkConfig(p => ({ ...p, differentialTitle: e.target.value }))}
                                            className="pm-form-input"
                                            placeholder="Como funciona?"
                                        />
                                    </div>
                                    <div className="pm-form-group">
                                        <label>Conteúdo do lado esquerdo</label>
                                        <DescBlockEditor
                                            value={linkConfig.differentialContent}
                                            onChange={v => setLinkConfig(p => ({ ...p, differentialContent: v }))}
                                        />
                                    </div>

                                    {/* Widget do lado direito */}
                                    <div className="pm-form-group" style={{ marginTop: 12 }}>
                                        <label>Widget do lado direito</label>
                                        <div className="pm-stats-mode-tabs">
                                            {([
                                                { id: 'none', label: 'Nenhum' },
                                                { id: 'animation_osce', label: 'Animação OSCE' },
                                                { id: 'stat_cards', label: 'Cards' }
                                            ] as const).map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    className={`pm-stats-mode-tab ${linkConfig.differentialRightMode === opt.id ? 'active' : ''}`}
                                                    onClick={() => setLinkConfig(p => ({ ...p, differentialRightMode: opt.id }))}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {linkConfig.differentialRightMode === 'animation_osce' && (
                                        <div className="pm-info-box" style={{ marginTop: 8 }}>
                                            <Info size={12} />
                                            <span>A animação interativa de cenário clínico OSCE será exibida no lado direito do bloco.</span>
                                        </div>
                                    )}

                                    {linkConfig.differentialRightMode === 'stat_cards' && (
                                        <div className="pm-stat-cards-editor">
                                            <p className="pm-field-hint" style={{ marginBottom: 10 }}>4 cards em grade 2×2 — ícone, número e rótulo:</p>
                                            {linkConfig.differentialStatCards.map((card, idx) => {
                                                const CardIconComp = DESC_ICONS.find(ic => ic.name === card.icon)?.component ?? BookOpen;
                                                return (
                                                    <div key={idx} className="pm-stat-card-row">
                                                        {/* Icon picker */}
                                                        <div className="pm-desc-icon-pick-wrap">
                                                            <button
                                                                type="button"
                                                                className={`pm-desc-icon-pick-btn ${statCardIconPicker === idx ? 'active' : ''}`}
                                                                onClick={() => setStatCardIconPicker(statCardIconPicker === idx ? null : idx)}
                                                                title="Trocar ícone"
                                                            >
                                                                <CardIconComp size={14} />
                                                            </button>
                                                            {statCardIconPicker === idx && (
                                                                <div className="pm-desc-icon-picker" style={{ minWidth: 220 }}>
                                                                    {['geral', 'medico', 'cardio'].map(group => (
                                                                        <React.Fragment key={group}>
                                                                            <div className="pm-desc-icon-group-label">{group === 'geral' ? 'Geral' : group === 'medico' ? 'Médico' : 'Cardio/Pneumo'}</div>
                                                                            <div className="pm-desc-icon-grid">
                                                                                {DESC_ICONS.filter(ic => ic.group === group).map(ic => {
                                                                                    const C = ic.component;
                                                                                    return (
                                                                                        <button key={ic.name} type="button"
                                                                                            className={`pm-desc-icon-opt ${card.icon === ic.name ? 'active' : ''}`}
                                                                                            title={ic.label}
                                                                                            onClick={() => {
                                                                                                const cards = [...linkConfig.differentialStatCards];
                                                                                                cards[idx] = { ...cards[idx], icon: ic.name };
                                                                                                setLinkConfig(p => ({ ...p, differentialStatCards: cards }));
                                                                                                setStatCardIconPicker(null);
                                                                                            }}>
                                                                                            <C size={13} />
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </React.Fragment>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <input
                                                            type="text"
                                                            className="pm-form-input pm-stat-card-value"
                                                            placeholder="Ex: 28"
                                                            value={card.value}
                                                            onChange={e => {
                                                                const cards = [...linkConfig.differentialStatCards];
                                                                cards[idx] = { ...cards[idx], value: e.target.value };
                                                                setLinkConfig(p => ({ ...p, differentialStatCards: cards }));
                                                            }}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="pm-form-input pm-stat-card-label"
                                                            placeholder="Ex: Apostilas"
                                                            value={card.label}
                                                            onChange={e => {
                                                                const cards = [...linkConfig.differentialStatCards];
                                                                cards[idx] = { ...cards[idx], label: e.target.value };
                                                                setLinkConfig(p => ({ ...p, differentialStatCards: cards }));
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Prévias ── */}
                            <div className="pm-link-section-label">Botão de Prévias</div>

                            <div className="pm-link-toggle-row pm-link-toggle-row-sm">
                                <div className="pm-link-toggle-text">
                                    <strong>Exibir botão de prévias</strong>
                                    <span>Mostra um botão secundário no hero da página de venda</span>
                                </div>
                                <button
                                    type="button"
                                    className={`pm-link-toggle-btn ${linkConfig.previewEnabled ? 'active' : ''}`}
                                    onClick={() => setLinkConfig(p => ({ ...p, previewEnabled: !p.previewEnabled }))}
                                >
                                    {linkConfig.previewEnabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                                </button>
                            </div>

                            {linkConfig.previewEnabled && (
                                <>
                                    <div className="pm-row-two" style={{ marginBottom: 0 }}>
                                        <div className="pm-form-group">
                                            <label>Texto do botão</label>
                                            <input
                                                type="text"
                                                value={linkConfig.previewButtonText}
                                                onChange={e => setLinkConfig(p => ({ ...p, previewButtonText: e.target.value }))}
                                                className="pm-form-input"
                                                placeholder="Acessar prévias grátis"
                                            />
                                        </div>
                                        <div className="pm-form-group">
                                            <label>URL de destino</label>
                                            <input
                                                type="text"
                                                value={linkConfig.previewButtonUrl}
                                                onChange={e => setLinkConfig(p => ({ ...p, previewButtonUrl: e.target.value }))}
                                                className="pm-form-input"
                                                placeholder="https://..."
                                            />
                                        </div>
                                    </div>
                                    <div className="pm-form-group">
                                        <label>Aviso abaixo do botão</label>
                                        <textarea
                                            value={linkConfig.previewNotice}
                                            onChange={e => setLinkConfig(p => ({ ...p, previewNotice: e.target.value }))}
                                            className="pm-form-textarea"
                                            rows={3}
                                            placeholder="Ex: Você pode acessar prévias acessando o material no campo de estudo..."
                                        />
                                    </div>
                                </>
                            )}

                            {/* ── Grid de conteúdo ── */}
                            <div className="pm-link-section-label">Layout do Conteúdo</div>
                            <div className="pm-grid-selector">
                                <button
                                    type="button"
                                    className={`pm-grid-option ${linkConfig.gridCols === 2 ? 'active' : ''}`}
                                    onClick={() => setLinkConfig(p => ({ ...p, gridCols: 2 }))}
                                >
                                    <Grid2X2 size={18} />
                                    <span>2 colunas</span>
                                </button>
                                <button
                                    type="button"
                                    className={`pm-grid-option ${linkConfig.gridCols === 3 ? 'active' : ''}`}
                                    onClick={() => setLinkConfig(p => ({ ...p, gridCols: 3 }))}
                                >
                                    <LayoutGrid size={18} />
                                    <span>3 colunas</span>
                                </button>
                            </div>

                            {/* ── Descrições de conteúdo ── botão abre modal dedicado */}
                            <div className="pm-link-section-label">Descrições de Conteúdo</div>
                            <button
                                type="button"
                                className="pm-btn-edit-descs"
                                onClick={() => {
                                    setLocalDescs({ ...linkConfig.subdeckDescriptions });
                                    setLocalIcons({ ...linkConfig.subdeckIcons });
                                    setLocalDeckDescs({ ...linkConfig.deckDescriptions });
                                    setLocalPreviewLinks({ ...(linkConfig.subdeckPreviewLinks || {}) });
                                    setOpenIconPickerFor(null);
                                    setShowDescsModal(true);
                                }}
                            >
                                <Edit2 size={14} />
                                {loadingContent
                                    ? 'Carregando subdecks...'
                                    : contentStructure.length > 0
                                        ? `Editar descrições (${contentStructure.reduce((a, d) => a + d.items.length, 0)} itens)`
                                        : 'Editar descrições de apostilas e subdecks'}
                            </button>
                        </div>

                        <div className="pm-modal-footer">
                            <button className="pm-btn-cancel" onClick={() => setShowLinkModal(false)}>Cancelar</button>
                            <button className="pm-btn-confirm" onClick={handleSaveLink} disabled={savingLink}>
                                {savingLink
                                    ? <><div className="pm-spinner-small" /> Salvando...</>
                                    : <><CheckCircle size={13} /> Salvar configuração</>
                                }
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ══ MODAL DELETE ══ */}
            {showDeleteModal && productToDelete && createPortal(
                <div className="pm-modal-overlay" onClick={() => !deleting && setShowDeleteModal(false)}>
                    <div className="pm-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>Confirmar Exclusão</h3>
                            <button className="pm-btn-close-modal" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="pm-modal-body">
                            <div className="pm-delete-icon"><AlertTriangle size={38} /></div>
                            <p className="pm-delete-text">Remover "<strong>{productToDelete.title}</strong>"?</p>
                            <div className="pm-warning-box">
                                <Info size={12} />
                                <span>Quem já comprou <strong>mantém o acesso</strong>.</span>
                            </div>
                        </div>
                        <div className="pm-modal-footer">
                            <button className="pm-btn-cancel" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                                Cancelar
                            </button>
                            <button className="pm-btn-danger" onClick={handleDeleteProduct} disabled={deleting}>
                                {deleting
                                    ? <><div className="pm-spinner-small" /> Removendo...</>
                                    : <><Trash2 size={13} /> Remover</>
                                }
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ══ MODAL DEDICADO: DESCRIÇÕES DE CONTEÚDO ══ */}
            {showDescsModal && createPortal(
                <div className="pm-modal-overlay" onClick={() => setShowDescsModal(false)}>
                    <div className="pm-modal-content pm-large pm-descs-modal" onClick={e => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>
                                <Edit2 size={15} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                Descrições de Conteúdo
                            </h3>
                            <button className="pm-btn-close-modal" onClick={() => setShowDescsModal(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="pm-modal-body">
                            <p className="pm-modal-hint" style={{ marginBottom: 16 }}>
                                Escreva uma descrição para cada apostila e subdeck. Será exibida na página pública de venda.
                            </p>

                            {loadingContent && (
                                <div className="pm-loading-combo" style={{ padding: '32px 0', justifyContent: 'center' }}>
                                    <div className="pm-spinner-small" />
                                    <span>Carregando apostilas e subdecks...</span>
                                </div>
                            )}

                            {!loadingContent && contentError && (
                                <div className="pm-desc-error">
                                    <p>{contentError}</p>
                                    <button type="button" className="pm-btn-retry"
                                        onClick={() => linkProduct && loadContentStructure(linkProduct._id)}>
                                        Tentar novamente
                                    </button>
                                </div>
                            )}

                            {!loadingContent && !contentError && contentStructure.length === 0 && (
                                <div className="pm-desc-empty">
                                    <p>Nenhum subdeck encontrado para este produto. Certifique-se de que o produto tem decks com subdecks associados.</p>
                                    <button type="button" className="pm-btn-retry"
                                        onClick={() => linkProduct && loadContentStructure(linkProduct._id)}>
                                        Recarregar
                                    </button>
                                </div>
                            )}

                            {!loadingContent && !contentError && contentStructure.length > 0 && (
                                <div className="pm-descriptions-list">
                                    {contentStructure.map(deck => (
                                        <div key={deck._id} className="pm-desc-deck">
                                            <div className="pm-desc-deck-name">{deck.name}</div>

                                            {/* Descrição do deck */}
                                            <div className="pm-desc-deck-desc-wrap">
                                                <div className="pm-desc-deck-desc-label">Descrição do deck (opcional)</div>
                                                <DescBlockEditor
                                                    value={localDeckDescs[deck._id] || ''}
                                                    onChange={val => setLocalDeckDescs(p => ({ ...p, [deck._id]: val }))}
                                                />
                                            </div>

                                            {deck.items.length === 0 ? (
                                                <div className="pm-desc-no-items">Nenhum subdeck neste deck</div>
                                            ) : deck.items.map(item => {
                                                const currentIcon = localIcons[item._id] || (item.type === 'apostila' ? 'BookOpen' : 'Layers');
                                                const CurrentIconComp = DESC_ICONS.find(ic => ic.name === currentIcon)?.component ?? BookOpen;
                                                const pickerKey = `item-${item._id}`;
                                                return (
                                                    <div key={item._id} className="pm-desc-item">
                                                        <div className="pm-desc-item-header">
                                                            {/* Ícone editável */}
                                                            <div className="pm-desc-icon-pick-wrap">
                                                                <button
                                                                    type="button"
                                                                    className={`pm-desc-icon-pick-btn ${openIconPickerFor === pickerKey ? 'active' : ''}`}
                                                                    onClick={() => setOpenIconPickerFor(openIconPickerFor === pickerKey ? null : pickerKey)}
                                                                    title="Trocar ícone"
                                                                >
                                                                    <CurrentIconComp size={14} />
                                                                </button>
                                                                {openIconPickerFor === pickerKey && (
                                                                    <div className="pm-desc-icon-picker">
                                                                        <div className="pm-desc-icon-group-label">Geral</div>
                                                                        <div className="pm-desc-icon-grid">
                                                                            {DESC_ICONS.filter(ic => ic.group === 'geral').map(ic => {
                                                                                const C = ic.component;
                                                                                return (
                                                                                    <button key={ic.name} type="button"
                                                                                        className={`pm-desc-icon-opt ${currentIcon === ic.name ? 'active' : ''}`}
                                                                                        title={ic.label}
                                                                                        onClick={() => {
                                                                                            setLocalIcons(p => ({ ...p, [item._id]: ic.name }));
                                                                                            setOpenIconPickerFor(null);
                                                                                        }}>
                                                                                        <C size={13} />
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                        <div className="pm-desc-icon-group-label">Médico</div>
                                                                        <div className="pm-desc-icon-grid">
                                                                            {DESC_ICONS.filter(ic => ic.group === 'medico').map(ic => {
                                                                                const C = ic.component;
                                                                                return (
                                                                                    <button key={ic.name} type="button"
                                                                                        className={`pm-desc-icon-opt ${currentIcon === ic.name ? 'active' : ''}`}
                                                                                        title={ic.label}
                                                                                        onClick={() => {
                                                                                            setLocalIcons(p => ({ ...p, [item._id]: ic.name }));
                                                                                            setOpenIconPickerFor(null);
                                                                                        }}>
                                                                                        <C size={13} />
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                        <div className="pm-desc-icon-group-label">Cardio / Pneumo</div>
                                                                        <div className="pm-desc-icon-grid">
                                                                            {DESC_ICONS.filter(ic => ic.group === 'cardio').map(ic => {
                                                                                const C = ic.component;
                                                                                return (
                                                                                    <button key={ic.name} type="button"
                                                                                        className={`pm-desc-icon-opt ${currentIcon === ic.name ? 'active' : ''}`}
                                                                                        title={ic.label}
                                                                                        onClick={() => {
                                                                                            setLocalIcons(p => ({ ...p, [item._id]: ic.name }));
                                                                                            setOpenIconPickerFor(null);
                                                                                        }}>
                                                                                        <C size={13} />
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <span className={`pm-desc-item-badge ${item.type === 'apostila' ? 'pm-badge-amber' : 'pm-badge-blue'}`}>
                                                                {item.type === 'apostila' ? 'Apostila' : 'Subdeck'}
                                                            </span>
                                                            <span className="pm-desc-item-name">{item.name}</span>
                                                            {(item.cardCount ?? 0) > 0 && (
                                                                <span className="pm-desc-item-count">{item.cardCount} cards</span>
                                                            )}
                                                        </div>
                                                        <DescBlockEditor
                                                            value={localDescs[item._id] || ''}
                                                            onChange={val => setLocalDescs(p => ({ ...p, [item._id]: val }))}
                                                        />
                                                        <div className="pm-preview-link-wrap">
                                                            <label className="pm-preview-link-label">
                                                                <ExternalLink size={12} />
                                                                Link de prévia (opcional)
                                                            </label>
                                                            <input
                                                                type="url"
                                                                className="pm-preview-link-input"
                                                                placeholder="https://..."
                                                                value={localPreviewLinks[item._id] || ''}
                                                                onChange={e => setLocalPreviewLinks(p => ({ ...p, [item._id]: e.target.value }))}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="pm-modal-footer">
                            <button className="pm-btn-cancel" onClick={() => setShowDescsModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className="pm-btn-confirm"
                                onClick={() => {
                                    setLinkConfig(p => ({
                                        ...p,
                                        subdeckDescriptions: localDescs,
                                        subdeckIcons: localIcons,
                                        deckDescriptions: localDeckDescs,
                                        subdeckPreviewLinks: localPreviewLinks
                                    }));
                                    setShowDescsModal(false);
                                }}
                            >
                                <CheckCircle size={14} /> Confirmar descrições
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CreateProducts;